import * as stylex from '@stylexjs/stylex';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { HoverTooltip } from '@/core/HoverTooltip.atom';
import { IconWithSpinner } from '@/core/IconWithSpinner.atom';
import { sqWorkbenchStore, sqWorkbookStore } from '@/core/core.stores';
import { useFluxPath } from '@/core/hooks/useFluxPath.hook';
import { useFluxPaths } from '@/core/hooks/useFluxPaths.hook';
import { SandboxModeBanner } from '@/header/SandboxModeBanner.molecule';
import { headerStyles as styles } from '@/header/header.styles';
import { addWorkbook } from '@/homescreen/homescreen.actions';
import { HOME_SCREEN_TABS } from '@/main/app.constants';
import { getWorksheetLink, goToWithReload } from '@/main/routing.utilities';
import { setShowModal } from '@/reportEditor/pdfExport.actions';
import { canModifyWorkbook as authServiceCanModifyWorkbook, isTrustedUser } from '@/services/authorization.service';
import { createUniqueCopyName, getTabFolder } from '@/utilities/homescreen.utilities';
import { errorToast, successToast } from '@/utilities/toast.utilities';
import { faCopy, faPencil } from '@seeqdev/icons/fa-sharp-regular-svg-icons';
import { fcPdfExport } from '@seeqdev/icons/seeq';
import { Icon } from '@seeqdev/qomponents';
import type { StoreWorkbookWithWorksheets } from '@/worksheet/worksheet.types';

interface ViewModeWorksheetActionsIF {
  isViewMode: boolean;
  isEditMode: boolean;
  isLocked: boolean;
}

export const ViewModeWorksheetActions = (props: ViewModeWorksheetActionsIF) => {
  const { isViewMode, isLocked } = props;

  const { t } = useTranslation();
  const [isCopying, setIsCopying] = useState(false);

  const { name, owner, effectivePermissions } = useFluxPaths(sqWorkbookStore, [
    'name',
    'owner',
    'effectivePermissions',
  ]);
  const workbook = {
    name,
    owner,
    effectivePermissions,
  };

  const canModifyWorkbook = authServiceCanModifyWorkbook(workbook);
  const isTopic = useFluxPath(sqWorkbookStore, (store) => store.isTopic);
  const isVantage = useFluxPath(sqWorkbookStore, (store) => store.isVantage);
  const isAnalysis = useFluxPath(sqWorkbookStore, (store) => store.isAnalysis);
  const workbookId = useFluxPath(sqWorkbookStore, (store) => store.workbookId);
  const isTrusted = useFluxPath(sqWorkbenchStore, (store) => store.currentUser.isTrusted);
  const navigate = useNavigate();

  const onClickBadge = () => {
    // We force a page load rather than routing. If we don't do this, state from view only mode can leak into edit mode,
    // unintentionally changing a workstep
    goToWithReload(getWorksheetLink(workbookId, sqWorkbenchStore.stateParams.worksheetId));
  };

  const duplicateToMyFolder = async () => {
    if (!isTrustedUser() || isCopying || !workbookId) return;

    setIsCopying(true);

    try {
      const myFolderTab: { id: string } | null = await getTabFolder(HOME_SCREEN_TABS.MY_FOLDER);
      if (!myFolderTab?.id) {
        throw new Error('My Folder not available');
      }

      const currentWorksheetId = sqWorkbenchStore.stateParams.worksheetId;
      const currentWorksheetIndex = sqWorkbookStore.worksheets.findIndex(
        (worksheet) => worksheet.worksheetId === currentWorksheetId,
      );

      const duplicate: StoreWorkbookWithWorksheets = await addWorkbook({
        name: createUniqueCopyName(name),
        branchFrom: workbookId,
        isTopic,
        isVantage,
        folderId: myFolderTab.id,
      });

      const duplicateWorksheetId =
        currentWorksheetIndex >= 0 ? duplicate.worksheets?.[currentWorksheetIndex]?.worksheetId : undefined;
      navigate(getWorksheetLink(duplicate.id, duplicateWorksheetId));
      requestAnimationFrame(() =>
        successToast({
          messageKey: 'SANDBOX_MODE.SUCCESS_MESSAGE',
          messageParams: { newWorkbook: duplicate.name },
        }),
      );
    } catch (error: unknown) {
      errorToast({ httpResponseOrError: error, displayForbidden: true });
    } finally {
      setIsCopying(false);
    }
  };

  const badgeTooltip = canModifyWorkbook ? 'VIEWING_ONLY.TOOLTIP' : 'VIEWING_ONLY.TOOLTIP_NO_ACCESS';

  return isViewMode ? (
    <div {...stylex.props(styles.viewModeActions)} data-testid="viewing-only-badge">
      <div data-testid="viewingOnlyBadge" {...stylex.props(styles.viewModeBadge)}>
        <HoverTooltip text={badgeTooltip} placement="bottom">
          <span {...stylex.props(styles.badgePadding)}>{t('VIEWING_ONLY.HEADER')}</span>
        </HoverTooltip>
      </div>
      <SandboxModeBanner />
      {isAnalysis && isTrusted && (
        <div {...stylex.props(styles.badgePadding)} onClick={duplicateToMyFolder}>
          <HoverTooltip text={t('VIEWING_ONLY.COPY_TO_MY_FOLDER')} placement="bottom">
            <IconWithSpinner
              icon={faCopy}
              spinning={isCopying}
              testId="viewOnlyCopyIcon"
              type="white"
              large={true}
              xstyle={[styles.copyIcon, isCopying && styles.copyPending, styles.navbarAction]}
            />
          </HoverTooltip>
        </div>
      )}

      {canModifyWorkbook && !isLocked && (
        <div {...stylex.props(styles.badgePadding)} onClick={onClickBadge}>
          <Icon
            icon={faPencil}
            tooltip={t('VIEWING_ONLY.EDIT')}
            tooltipPlacement="bottom"
            size="lg"
            type="theme-light"
            testId="viewOnlyEditIcon"
            xstyle={[styles.actionTrigger, styles.themedNavbarAction]}
          />
        </div>
      )}
      {isTopic && (
        <span {...stylex.props(styles.exportAction, styles.navbarAction)}>
          <Icon
            xstyle={styles.exportIcon}
            icon={fcPdfExport}
            tooltip={t('REPORT.EDITOR.SAVE_AS_PDF_BUTTON_TITLE')}
            tooltipPlacement="bottom"
            type="theme-light"
            large={true}
            onClick={() => setShowModal(true)}
            testId="pdfExportIcon"
          />
        </span>
      )}
    </div>
  ) : null;
};
